import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { CreateManualPortfolio } from '../components/CreateManualPortfolio';
import { UpdateManualPortfolio } from '../components/UpdateManualPortfolio';

import type { Portfolio, Holding } from '@/hooks/portfolio/usePortfolio';

export type EditableHoldingFields = 'units' | 'averagePrice';

export type PortfolioToUpdate = {
  portfolioId: Portfolio['id'];
  holdingId?: Holding['id'];
  holdingFieldToUpdate?: EditableHoldingFields;
};

type Context = {
  createPortfolio: () => void;
  updatePortfolio: (portfolio: PortfolioToUpdate) => void;
};

interface Props {
  children: React.ReactNode;
}

const ManualPortfolioContext = createContext<Context | null>(null);

export const ManualPortfolioProvider = ({ children }: Props) => {
  const [state, setState] = useState<'idle' | 'create' | 'update'>('idle');
  const [portfolio, setPortfolioData] = useState<PortfolioToUpdate | null>(
    null
  );

  const handleClose = useCallback(() => {
    setState('idle');
    setPortfolioData(null);
  }, []);

  const value = useMemo(
    () => ({
      createPortfolio: () => setState('create'),
      updatePortfolio: (portfolio: PortfolioToUpdate) => {
        setPortfolioData(portfolio);
        setState('update');
      },
    }),
    []
  );

  return (
    <ManualPortfolioContext.Provider value={value}>
      {children}
      {state === 'create' && <CreateManualPortfolio onClose={handleClose} />}
      {state === 'update' && portfolio !== null && (
        <UpdateManualPortfolio initialData={portfolio} onClose={handleClose} />
      )}
    </ManualPortfolioContext.Provider>
  );
};

export function useManualPortfolioContext() {
  const context = useContext(ManualPortfolioContext);

  if (context) {
    return context;
  }

  throw new Error(
    'Attempted to access ManualPortfolioContext outside of a ManualPortfolioProvider'
  );
}
